import React, { useEffect, useState } from 'react';

import styles from './TimeInput.module.css';

type TimeInputProps = {
  defaultValue?: string;
  disabled?: boolean;
  dataTestId?: string;
  onChange?: (value: string) => void;
};

export function TimeInput({
  defaultValue,
  dataTestId,
  disabled,
  onChange,
  ref,
}: TimeInputProps & { ref?: React.Ref<HTMLInputElement> }) {
  const [value, setValue] = useState(defaultValue ?? '00:00');

  const handleKeyDown = (e) => {
    let cursorPosition = e.target.selectionStart;
    let [hours, minutes] = value.split(':').map(Number);
    const colonPosition = value.indexOf(':');

    if (e.key === 'ArrowUp') {
      if (cursorPosition <= colonPosition) {
        hours = (hours + 1) % 25; // max 24 uur
      } else {
        minutes = (minutes + 1) % 60;
      }
    } else if (e.key === 'ArrowDown') {
      if (cursorPosition <= colonPosition) {
        hours = (hours - 1 + 25) % 25; // Max 24 uur
      } else {
        minutes = (minutes - 1 + 60) % 60;
      }
    } else if (e.key === 'ArrowLeft') {
      cursorPosition = Math.max(cursorPosition - 1, 0);
      // Verplaats de cursor één positie naar links als deze zich op de dubbele punt positie bevindt
      if (cursorPosition === colonPosition) {
        cursorPosition -= 1;
      }
    } else if (e.key === 'ArrowRight') {
      cursorPosition = Math.min(cursorPosition + 1, value.length);
      // Verplaats de cursor één positie naar rechts als deze zich op de dubbele punt positie bevindt
      if (cursorPosition === colonPosition) {
        cursorPosition += 1;
      }
    } else if (e.key === 'Backspace') {
      cursorPosition = Math.max(cursorPosition - 1, 0);
      // Verplaats de cursor één positie naar links als deze zich op de dubbele punt positie bevindt
      if (cursorPosition === colonPosition) {
        cursorPosition -= 1;
      }
    } else {
      cursorPosition = Math.min(cursorPosition + 1, value.length + 1);
      // Verplaats de cursor één positie naar rechts als deze zich op de dubbele punt positie bevindt
      if (cursorPosition === colonPosition) {
        cursorPosition += 1;
      }
    }

    const newValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    setValue(newValue);
    // behoud de cursor positie
    setTimeout(() => {
      e.target.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleChange = (e) => {
    let inputValue = e.target.value.replace(/[^0-9]/g, ''); // Verwijder alle niet-numerieke karakters
    if (inputValue.length > 4) {
      inputValue = inputValue.slice(0, 4); // max 4 chars
    }
    if (inputValue.length > 2) {
      inputValue = `${inputValue.slice(0, -2)}:${inputValue.slice(-2)}`; // zet een ':' tussen de uren en minuten
    }

    let [hours, minutes] = inputValue.split(':').map(Number);
    if (hours > 24) {
      hours = 24; // max 24 uur
    }
    setValue(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
  };

  useEffect(() => {
    onChange?.(value);
  }, [onChange, value]);

  return (
    <input
      ref={ref}
      type='text'
      className={styles.timeInputStyle}
      value={value}
      disabled={disabled}
      pattern='^([0-1][0-9]|2[0-4]):[0-5][0-9]$'
      title='Format: 00:00'
      placeholder='00:00'
      data-testid={dataTestId}
      required
      onKeyDown={handleKeyDown}
      onChange={handleChange}
    />
  );
}

export default TimeInput;
